import { useMemo } from 'react';

import { type ApolloError, useQuery } from '@apollo/client';
import { EnterpriseContractPrice_PricingModel as pricingModel } from '__generated__/graphql-types';
import type { EnterprisePromotion_PromotionStage as promotionStage } from '__generated__/graphql-types';

import { get } from 'js/lib/user';

import EnterpriseSelfServeProductPrice from 'bundles/wes-common/queries/EnterpriseSelfServeProductPrice.graphql';
import type {
  EnterpriseSelfServeProductPriceQuery,
  EnterpriseSelfServeProductPriceQueryVariables,
} from 'bundles/wes-common/queries/__generated__/EnterpriseSelfServeProductPrice';
import type { ProductPrice } from 'bundles/wes-common/types';

type UseProductPriceParams = {
  learnerCount?: number;
  billingType?: number;
  enterprisePromotionId?: string;
  skipApiCall?: boolean;
  isDifferentialPricingEnabled?: boolean;
  stage?: promotionStage;
};

type UseProductPriceReturn = {
  productPrice: ProductPrice | null;
  defaultProductPrice: ProductPrice | null;
  loading: boolean;
  error?: ApolloError;
};

// Transform GraphQL data to match ProductPrice structure
function transformProductPriceData(
  data: EnterpriseSelfServeProductPriceQuery | undefined
): Array<ProductPrice> | undefined {
  return data?.EnterpriseContractPriceQueries?.getEnterpriseSelfServeContractProductPrice?.elements?.map(
    (element): ProductPrice => ({
      unitPrice: element.price.unitPrice.toString(),
      referenceUnitPrice: element.price.referenceUnitPrice?.toString() ?? null,
      rawLocalAmountTotal: element.price.rawLocalAmountTotal.toString(),
      discountPercent: element.price.discountPercent,
      countryIsoCode: element.price.countryIsoCode,
      actualLocalAmountDueToday: element.price.actualLocalAmountDueToday.toString(),
      actualLocalAmountTotal: element.price.actualLocalAmountTotal.toString(),
      rawLocalAmountDueToday: element.price.rawLocalAmountDueToday.toString(),
      actualLocalDiscountedAmount: element.price.actualLocalDiscountedAmount.toString(),
      rawAmountTotal: element.price.rawAmountTotal.toString(),
      rawAmountDueToday: element.price.rawAmountDueToday.toString(),
      currencyCode: element.price.currencyCode,
      id: `${element.id.userLimit}-${element.id.billingType}`, // Construct ID
    })
  );
}

/**
 * @deprecated with tiered pricing,
 * use useEnterpriseSelfServePriceV2 instead
 */
export const useEnterpriseSelfServePrice = ({
  learnerCount,
  billingType,
  enterprisePromotionId,
  skipApiCall,
  isDifferentialPricingEnabled,
  stage,
}: UseProductPriceParams): UseProductPriceReturn => {
  const userId = get()?.id ?? 0;

  // GraphQL Query approach
  // Localised pricing is always enabled - passing null for currencyCode lets the backend
  // determine the appropriate currency based on user's location
  const {
    loading: gqlLoading,
    error: gqlError,
    data: graphqlData,
  } = useQuery<EnterpriseSelfServeProductPriceQuery, EnterpriseSelfServeProductPriceQueryVariables>(
    EnterpriseSelfServeProductPrice,
    {
      variables: {
        userLimit: learnerCount ?? 1,
        termInstallmentsTotal: billingType ?? 1,
        enterprisePromotionId,
        countryIsoCode: '',
        currencyCode: null, // Localised pricing - backend determines currency based on user location
        userId: userId.toString(),
        pricingModel: isDifferentialPricingEnabled ? pricingModel.DifferentialQuarterlyPremium : pricingModel.Standard,
        stage,
      },
      context: { clientName: 'gatewayGql' },
      skip: skipApiCall,
    }
  );

  const transformedGraphQLData = useMemo(() => transformProductPriceData(graphqlData), [graphqlData]);

  // Return unified interface - both productPrice and defaultProductPrice use the same data
  // Backend has unnecessary pagination structure, so we just return the first element until they fix it
  return {
    productPrice: transformedGraphQLData?.[0] ?? null,
    defaultProductPrice: transformedGraphQLData?.[0] ?? null,
    loading: gqlLoading,
    error: gqlError,
  };
};
